import React, { useState } from "react";
import MobileBottomNav from "../components/common/MobileBottomNav";
import GenericModal from "../components/common/GenericModal";
import {
  MapPin,
  Briefcase,
  Clock,
  IndianRupee,
  Eye,
  CheckCircle,
  Loader2,
} from "lucide-react";
import toast from "react-hot-toast";

// Dummy data that would come from an API.
const dummyJobs = [
  {
    id: 1,
    title: "Senior Civil Engineer",
    company: "Prestige Constructions",
    location: "Chennai, Tamil Nadu",
    postedDate: "2 days ago",
    positions: "2",
    experience: "5–8 years",
    jobType: "Full time",
    workDays: "6",
    shiftTiming: "9 AM - 6 PM",
    salaryAmount: "75000",
    salaryType: "month",
    benefits: {
      accommodation: true,
      food: false,
      insurance: true,
      travel: false,
      allowance: true,
    },
    gender: "Any",
    ageLimit: "45",
    description:
      "Looking for an experienced Senior Civil Engineer to oversee large-scale construction projects. Must have a proven track record of managing teams and delivering projects on time and within budget. Responsibilities include project planning, resource allocation, and ensuring quality standards.",
    notes: "Immediate joiners preferred. PMP certification is a plus.",
  },
  {
    id: 2,
    title: "Site Supervisor",
    company: "L&T Realty",
    location: "Coimbatore, Tamil Nadu",
    postedDate: "5 days ago",
    positions: "5",
    experience: "3–5 years",
    jobType: "Contract",
    workDays: "6",
    shiftTiming: "8 AM - 7 PM",
    salaryAmount: "25000",
    salaryType: "month",
    benefits: {
      accommodation: true,
      food: true,
      insurance: false,
      travel: true,
      allowance: false,
    },
    gender: "Male",
    ageLimit: "35",
    description:
      "We are seeking a diligent Site Supervisor to manage daily on-site operations. You will be responsible for coordinating with subcontractors, ensuring safety protocols are followed, and reporting progress to the project manager.",
    notes: "Must be willing to work on weekends if required.",
  },
];

const JobPortal = () => {
  const [jobs] = useState(dummyJobs);
  const [selectedJob, setSelectedJob] = useState(null);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [isApplyModalOpen, setIsApplyModalOpen] = useState(false);
  const [applicationForm, setApplicationForm] = useState({
    name: "",
    email: "",
    phone: "",
    resume: null,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const openDetailsModal = (job) => {
    setSelectedJob(job);
    setIsDetailsModalOpen(true);
  };

  const openApplyModal = (job) => {
    setSelectedJob(job);
    setIsApplyModalOpen(true);
  };

  const handleApplyChange = (e) => {
    const { name, value, files } = e.target;
    if (name === "resume") {
      setApplicationForm((prev) => ({ ...prev, resume: files[0] }));
    } else {
      setApplicationForm((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleApplySubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    // API call would go here
    setTimeout(() => {
      toast.success(`Successfully applied for ${selectedJob.title}!`);
      setIsSubmitting(false);
      setIsApplyModalOpen(false);
      setApplicationForm({ name: "", email: "", phone: "", resume: null });
    }, 1500);
  };

  const getBenefitText = (benefits) => {
    return Object.entries(benefits)
      .filter(([, value]) => value)
      .map(([key]) => key.charAt(0).toUpperCase() + key.slice(1))
      .join(", ");
  };

  return (
    <div className="bg-gray-50 pb-16 lg:pb-0 min-h-screen">
      <section className="py-12 bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="container mx-auto px-6 text-center">
          <h1 className="text-xl md:text-2xl font-bold text-blue-600 uppercase mb-4">
            Find Your Next Job
          </h1>
          <p className="text-sm md:text-base text-gray-600 max-w-2xl mx-auto leading-relaxed">
            Explore exciting career opportunities in the construction industry.
          </p>
        </div>
      </section>

      <section className="container mx-auto px-6 py-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {jobs.map((job) => (
            <div
              key={job.id}
              className="bg-white rounded-lg shadow-md p-6 flex flex-col hover:shadow-xl transition-shadow"
            >
              <h2 className="text-base md:text-lg font-bold text-gray-800 mb-1">
                {job.title}
              </h2>
              <p className="text-sm font-medium text-blue-600">{job.company}</p>
              <div className="text-xs text-gray-500 mt-2 space-y-1">
                <p className="flex items-center gap-2">
                  <MapPin className="w-4 h-4" /> {job.location}
                </p>
                <p className="flex items-center gap-2">
                  <Briefcase className="w-4 h-4" /> {job.experience}
                </p>
                <p className="flex items-center gap-2">
                  <IndianRupee className="w-4 h-4" /> ₹{job.salaryAmount} per{" "}
                  {job.salaryType}
                </p>
              </div>
              <div className="mt-4 pt-4 border-t border-gray-100 flex justify-between items-center">
                <span className="text-xs text-gray-400">
                  Posted {job.postedDate}
                </span>
                <button
                  onClick={() => openDetailsModal(job)}
                  className="text-sm text-blue-500 font-semibold hover:underline flex items-center gap-1"
                >
                  <Eye className="w-4 h-4" /> View Details
                </button>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Job Details Modal */}
      <GenericModal
        isOpen={isDetailsModalOpen}
        onClose={() => setIsDetailsModalOpen(false)}
        title={selectedJob?.title || "Job Details"}
        size="max-w-2xl"
      >
        {selectedJob && (
          <div className="space-y-4 text-sm">
            <p>
              <strong className="font-semibold text-gray-700">Company:</strong>{" "}
              {selectedJob.company}
            </p>
            <p>
              <strong className="font-semibold text-gray-700">Location:</strong>{" "}
              {selectedJob.location}
            </p>
            <div className="grid grid-cols-2 gap-4">
              <p>
                <strong className="font-semibold text-gray-700">
                  Experience:
                </strong>{" "}
                {selectedJob.experience}
              </p>
              <p>
                <strong className="font-semibold text-gray-700">
                  Job Type:
                </strong>{" "}
                {selectedJob.jobType}
              </p>
              <p>
                <strong className="font-semibold text-gray-700">Salary:</strong>{" "}
                ₹{selectedJob.salaryAmount} / {selectedJob.salaryType}
              </p>
              <p>
                <strong className="font-semibold text-gray-700">
                  Work Days:
                </strong>{" "}
                {selectedJob.workDays} days/week
              </p>
              <p>
                <strong className="font-semibold text-gray-700">Gender:</strong>{" "}
                {selectedJob.gender}
              </p>
              <p>
                <strong className="font-semibold text-gray-700">
                  Age Limit:
                </strong>{" "}
                {selectedJob.ageLimit} years
              </p>
            </div>
            <p>
              <strong className="font-semibold text-gray-700">Benefits:</strong>{" "}
              {getBenefitText(selectedJob.benefits) || "None"}
            </p>
            <div>
              <strong className="font-semibold text-gray-700">
                Description:
              </strong>
              <p className="mt-1 text-gray-600 whitespace-pre-wrap">
                {selectedJob.description}
              </p>
            </div>
            {selectedJob.notes && (
              <div>
                <strong className="font-semibold text-gray-700">
                  Additional Notes:
                </strong>
                <p className="mt-1 text-gray-600">{selectedJob.notes}</p>
              </div>
            )}
            <div className="mt-6 flex justify-end">
              <button
                onClick={() => {
                  setIsDetailsModalOpen(false);
                  openApplyModal(selectedJob);
                }}
                className="bg-blue-600 text-white font-semibold py-2 px-6 rounded-lg hover:bg-blue-700 transition"
              >
                Apply Now
              </button>
            </div>
          </div>
        )}
      </GenericModal>

      {/* Application Modal */}
      <GenericModal
        isOpen={isApplyModalOpen}
        onClose={() => setIsApplyModalOpen(false)}
        title={`Apply for ${selectedJob?.title || ""}`}
      >
        <form onSubmit={handleApplySubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Full Name
            </label>
            <input
              type="text"
              name="name"
              value={applicationForm.name}
              onChange={handleApplyChange}
              className="mt-1 block w-full p-2 border border-gray-300 rounded-md"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Email Address
            </label>
            <input
              type="email"
              name="email"
              value={applicationForm.email}
              onChange={handleApplyChange}
              className="mt-1 block w-full p-2 border border-gray-300 rounded-md"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Phone Number
            </label>
            <input
              type="tel"
              name="phone"
              value={applicationForm.phone}
              onChange={handleApplyChange}
              className="mt-1 block w-full p-2 border border-gray-300 rounded-md"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Upload Resume
            </label>
            <input
              type="file"
              name="resume"
              onChange={handleApplyChange}
              className="mt-1 block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
              required
            />
          </div>
          <div className="flex justify-end pt-2">
            <button
              type="submit"
              disabled={isSubmitting}
              className="bg-green-500 text-white font-semibold py-2 px-6 rounded-lg hover:bg-green-600 transition flex items-center gap-2 disabled:opacity-60"
            >
              {isSubmitting ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <CheckCircle className="w-5 h-5" />
              )}
              {isSubmitting ? "Submitting..." : "Submit Application"}
            </button>
          </div>
        </form>
      </GenericModal>

      <MobileBottomNav />
    </div>
  );
};

export default JobPortal;
